import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { mlmAPI } from '../services/api';
import { DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle, Filter } from 'lucide-react';
import AnimatedCounter from '../components/common/AnimatedCounter';

const Commissions = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await mlmAPI.getCommissionHistory({ limit: 100 });
        const list = Array.isArray(res.data) ? res.data : res.data?.commissions || res.data?.history || [];
        setHistory(list);
      } catch {
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredHistory = filter === 'all' ? history : history.filter((c) => c.type?.toLowerCase() === filter);

  const totals = {
    direct: history.filter(c => c.type === 'direct').reduce((s, c) => s + (c.amount || 0), 0),
    binary: history.filter(c => c.type === 'binary').reduce((s, c) => s + (c.amount || 0), 0),
    residual: history.filter(c => c.type === 'residual').reduce((s, c) => s + (c.amount || 0), 0),
    bonus: history.filter(c => c.type === 'bonus').reduce((s, c) => s + (c.amount || 0), 0),
    total: history.reduce((s, c) => s + (c.amount || 0), 0),
  };

  const summaryCards = [
    { label: 'Comisiones Directas', value: totals.direct || 0, color: 'from-orange-500 to-amber-500' },
    { label: 'Comisiones Binarias', value: totals.binary || 0, color: 'from-purple-500 to-pink-500' },
    { label: 'Comisiones Residuales', value: totals.residual || 0, color: 'from-blue-500 to-cyan-500' },
    { label: 'Bonos', value: totals.bonus || 0, color: 'from-green-500 to-emerald-500' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mis Comisiones</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">Historial y desglose de tus ganancias</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
          >
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-3`}>
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              <AnimatedCounter value={card.value} decimals={2} prefix="$" />
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Todas las comisiones</option>
            <option value="directa">Directas</option>
            <option value="binaria">Binarias</option>
            <option value="residual">Residuales</option>
            <option value="bono">Bonos</option>
          </select>
        </div>
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="ml-auto bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg shadow-orange-500/25"
        >
          Total: <AnimatedCounter value={totals.total || 0} decimals={2} prefix="$" />
        </motion.div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Origen</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nivel</th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Monto</th>
                <th className="text-center px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredHistory.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {history.length === 0 ? 'No hay comisiones registradas aún' : 'No hay comisiones para este filtro'}
                  </td>
                </tr>
              ) : (
                filteredHistory.map((c, i) => (
                  <motion.tr
                    key={c.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {c.date || c.created_at ? new Date(c.date || c.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                        c.type?.toLowerCase() === 'directa' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' :
                        c.type?.toLowerCase() === 'binaria' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                        c.type?.toLowerCase() === 'residual' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {c.type || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{c.from_user || c.origin || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">Nivel {c.level || '—'}</td>
                    <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900 dark:text-white">
                      ${(c.amount || c.commission || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {c.status === 'paid' || c.is_paid ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 dark:bg-green-900/30 px-2.5 py-1 rounded-full">
                          <CheckCircle className="w-3 h-3" /> Pagado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 px-2.5 py-1 rounded-full">
                          <Clock className="w-3 h-3" /> Pendiente
                        </span>
                      )}
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default Commissions;
